// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--rovo-expansion/rovo-chat-input-side-panel';

import { MaxSideNavBanner } from '../../../common/components/max-side-nav-banner';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type MaxSideNavBannerMessageProps = {
	onTryMax?: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<PlacementRenderProps>(),
		Component: ({ dismiss, maxSideNavBannerMessageProps }: PlacementRenderProps) => {
			const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();
			const { messageDismissed } = useMessageEvent();
			const { onTryMax } =
				(maxSideNavBannerMessageProps as MaxSideNavBannerMessageProps | undefined) ?? {};

			const handleDismiss = useCallback(async () => {
				await messageDismissed();
				void onClosed({
					interactionIdentifier: 'rovo-chat-long-running-task-side-panel-banner-dismiss',
					additionalMetadata: {
						maxIntroSuppressionSource: 'side-nav-banner-dismiss',
					},
				});

				dismiss();
			}, [dismiss, messageDismissed, onClosed]);

			const handleTryMax = useCallback(() => {
				try {
					void onCtaActioned({
						interactionIdentifier: 'rovo-chat-long-running-task-side-panel-banner-try-max',
						additionalMetadata: {
							maxIntroSuppressionSource: 'side-nav-banner-try-max',
						},
					});
				} finally {
					onTryMax?.();
					dismiss();
				}
			}, [dismiss, onCtaActioned, onTryMax]);

			return <MaxSideNavBanner onDismiss={handleDismiss} onTryMax={handleTryMax} />;
		},
	},
});
